/**
 * @file Main Video Grid component.
 *
 * @description High level component responsible for displaying large sets
 * of video records in a justified grid using windowing (with React Virtualized).
 *
 * Pretty much a clone of the PhotoGrid with a route for videos and a video viewer
 * instead, as well as updated react code.
 */

import React, { useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { WindowScroller, AutoSizer, Collection } from 'react-virtualized';
import justifiedLayout from 'justified-layout';
import memoize from 'fast-memoize';
import get from 'lodash.get';
import { Route, withRouter } from 'react-router-dom';

import { getImageRatioFromThumbnails } from '../../util/photo';
import { PhotoGridScrollContext } from './PhotoGrid';
import PhotoGridScrollLoader from './components/PhotoGridScrollLoader';
import VideoGridViewer from './components/VideoGridViewer';
import VideoDownloadModal from './components/VideoDownload';

const propTypes = {
  loading: PropTypes.bool,
  records: PropTypes.arrayOf(PropTypes.object),
  total: PropTypes.number,
  loadMore: PropTypes.func,
  cellRenderer: PropTypes.func.isRequired,
  children: PropTypes.func,
  loadingComponent: PropTypes.node,
  recordVideoAccessor: PropTypes.string,
  // This width is used for SSR and is irrelevant as when the
  // component mounts will auto-update to the correct dimensions
  // given the existance of the real DOM nodes.
  defaultContainerWidth: PropTypes.number,
  defaultRecordRatio: PropTypes.number,
  targetRowHeight: PropTypes.number,
  windowScrollerProps: PropTypes.object,
  justifiedLayoutOptions: PropTypes.shape({
    containerWidth: PropTypes.number,
    containerPadding: PropTypes.number,
    boxSpacing: PropTypes.number,
    targetRowHeightTolerance: PropTypes.number,
    maxNumRows: PropTypes.number,
    forceAspectRatio: PropTypes.bool,
    showWidows: PropTypes.bool,
    fullWidthBreakoutRowCadence: PropTypes.bool,
    widowLayoutStyle: PropTypes.oneOf(['justify', 'center', 'left']),
  }),
  match: PropTypes.shape({
    path: PropTypes.string,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  videoViewerProps: PropTypes.shape({
    actions: PropTypes.func,
    imageSizeCode: PropTypes.string,
    renderVideo: PropTypes.func,
  }),
};

const VideoGrid = ({
  loading,
  records = [],
  total,
  loadMore,
  cellRenderer,
  children = () => {},
  loadingComponent,
  recordVideoAccessor,
  // This width is used for SSR and is irrelevant as when the
  // component mounts will auto-update to the correct dimensions
  // given the existance of the real DOM nodes.
  defaultContainerWidth = 1060,
  defaultRecordRatio = 1,
  targetRowHeight = 280,
  windowScrollerProps = {},
  justifiedLayoutOptions,
  match: { path, url },
  location,
  videoViewerProps = {},
}) => {
  const collection = useRef(undefined);
  const indicesRendered = useRef(undefined);

  const onGridResize = () => {
    if (collection.current) {
      collection.current.recomputeCellSizesAndPositions();
    }
  };

  const onSectionRendered = ({ indices }) => {
    indicesRendered.current = indices;
  };

  // `secondaryCache` here just so that memoization happens
  // based on last 10 record Ids & the records length instead of only `width` as
  // we need this function to recompute all box data upon
  // loading new records

  // Moved on from using records.length as secondary cache
  // as it would not update box data when the count is the same
  // but contents have changed.
  //
  // eslint-disable-next-line no-unused-vars
  const getBoxData = memoize((containerWidth, secondaryCache) => {
    // Using reduce here and not map+filter as we want
    // to exclude records without ratios/thumbnails in one pass
    const ratios = records.reduce((acc, record) => {
      const video = recordVideoAccessor
        ? get(record, recordVideoAccessor)
        : record;

      const ratio =
        getImageRatioFromThumbnails(video.thumbnail_urls) || defaultRecordRatio;

      acc.push(ratio);
      return acc;
    }, []);

    return justifiedLayout(ratios, {
      containerWidth: containerWidth || defaultContainerWidth,
      targetRowHeight,
      targetRowHeightTolerance: 0.175,
      boxSpacing: 2,
      containerPadding: 0,
      ...justifiedLayoutOptions,
    });
  });

  if (loading) {
    return loadingComponent || null;
  }

  const memoizationKey = [...records.slice(-10), ...records.slice(10)].reduce(
    (acc, item) => (item.id || item?.[recordVideoAccessor]?.id) + acc,
    `${records.length}-${targetRowHeight}`,
  );
  const shouldLoadMore = total > records.length;

  return (
    <Fragment>
      <Route
        path={`${path}/v/:recordId`}
        exact
        render={({ match }) => (
          <VideoGridViewer
            recordId={match.params.recordId}
            records={records}
            loadMore={loadMore}
            shouldLoadMore={shouldLoadMore}
            recordVideoAccessor={recordVideoAccessor}
            gridPath={url}
            {...videoViewerProps}
          />
        )}
      />

      <PhotoGridScrollContext.Consumer>
        {mainContainerRef => (
          <WindowScroller
            // mainContainerRef.current might be null, which breaks the photoGrid.
            // We need to pass undefined as an alternative just in case.
            scrollElement={mainContainerRef?.current || undefined}
            {...windowScrollerProps}
          >
            {({
              height, // Viewport height
              isScrolling,
              scrollTop,
            }) => {
              return (
                <AutoSizer
                  disableHeight
                  onResize={onGridResize}
                  height={height}
                  defaultWidth={defaultContainerWidth}
                >
                  {({ width }) => {
                    const { boxes } = getBoxData(width, memoizationKey);

                    return (
                      <div style={{ width: `${width}px` }}>
                        <Collection
                          ref={collection}
                          width={width}
                          height={height}
                          autoHeight
                          scrollTop={scrollTop}
                          onSectionRendered={onSectionRendered}
                          cellCount={boxes.length}
                          verticalOverscanSize={height * 2}
                          cellSizeAndPositionGetter={({ index }) => ({
                            height: boxes[index].height,
                            width: boxes[index].width,
                            x: boxes[index].left,
                            y: boxes[index].top,
                          })}
                          cellRenderer={({ index, key, style }) => {
                            const record = records[index];
                            const recordPath = `${location.pathname}/v/${record.id}`;

                            return (
                              <div key={key} style={style}>
                                {cellRenderer({
                                  record,
                                  index,
                                  isScrolling,
                                  recordPath,
                                })}
                              </div>
                            );
                          }}
                        />

                        {children({
                          height,
                          width,
                          scrollTop,
                          isScrolling,
                          boxes,
                          indicesRendered: indicesRendered.current,
                        })}

                        {loadMore && (
                          <PhotoGridScrollLoader
                            loadMore={loadMore}
                            shouldLoadMore={shouldLoadMore}
                            onMoreLoaded={onGridResize}
                            recordsLength={records.length}
                            indicesRendered={indicesRendered.current}
                          />
                        )}
                      </div>
                    );
                  }}
                </AutoSizer>
              );
            }}
          </WindowScroller>
        )}
      </PhotoGridScrollContext.Consumer>
      <VideoDownloadModal />
    </Fragment>
  );
};

VideoGrid.propTypes = propTypes;

export default withRouter(VideoGrid);
